import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import Container from '@magoosh/layouts/components/container';
import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';

import UnderlinedText from '@marketing/components/underlined_text';
import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';
import { formatOfferPrice } from '@marketing/utilities/offer_price';

import AISection from './sections/ai';
import AppsSection from './sections/apps';
import EducatorsSection from './sections/educators';
import FeaturesSection from './sections/features';
import InstructorsSection from './sections/instructors';
import ValuePropsSection from './sections/value_props';
import VideoBasedLearningSection from './sections/video_based_learning';
import WallOfLoveSection from './sections/wall_of_love';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import GuaranteeSection from '../sections/guarantee';
import HeroSection from '../sections/hero';
import SocialProofSection from '../sections/social_proof';

import marketingStyles from '@marketing/style.module.scss';
import styles from '../style.module.scss';

interface Props extends PageProps {
  startingOffer: OfferProps;
}

export default (props: Props) => {
  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.purpleGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />

        <Container>
          <div style={{ position: 'absolute', left: 0, top: -25, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzOTAiIGhlaWdodD0iNDExIiBmaWxsPSJub25lIj48bWFzayBpZD0iYiIgd2lkdGg9IjQ2MCIgaGVpZ2h0PSI0NTYiIHg9Ii05OCIgeT0iLTE0NiIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNhKSIgZD0iTTIxNi41MTYtMTQ1LjExMmgzMzh2MzQ4aC0zMzh6IiB0cmFuc2Zvcm09InJvdGF0ZSg2NC42MTMgMjE2LjUxNiAtMTQ1LjExMikiLz48L21hc2s+PGcgc3Ryb2tlPSIjQjY3NEZGIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIHN0cm9rZS13aWR0aD0iNjMuNzUiIG1hc2s9InVybCgjYikiIG9wYWNpdHk9Ii4yIj48Y2lyY2xlIGN4PSIxNjEuMDc5IiBjeT0iMTYxLjA3OSIgcj0iMTI5LjIwNCIgb3BhY2l0eT0iLjIiIHRyYW5zZm9ybT0ic2NhbGUoMSAtMSkgcm90YXRlKC0xNS4xMTkgLTYyMC4yNjIgMjc3LjMwNSkiLz48cGF0aCBkPSJNMTc3LjkwNCAxNDQuNDhBMTI5LjIgMTI5LjIgMCAwIDAgMTUwLjQ2LTgxLjc1NmExMjkuMjA0IDEyOS4yMDQgMCAwIDAtOTEuMDE5LS45MjciIG9wYWNpdHk9Ii4yIi8+PHBhdGggZD0iTTIxOS44NjggOTUuMjg1YTEyOS4yMiAxMjkuMjIgMCAwIDAgNS45OTQtOTguNzA2IDEyOS4yMTEgMTI5LjIxMSAwIDAgMC02NS41NTgtNzQuMDM0IiBvcGFjaXR5PSIuMiIvPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSI1NTQuNTE2IiB4Mj0iMjE2LjUxNiIgeTE9IjI4Ljg4OCIgeTI9IjI4Ljg4OCIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>

          <div className="hidden-xs hidden-sm" style={{ position: 'absolute', right: -50, zIndex: 1 }}>
            <img
              alt="Background image of purple circle radius"
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NDQiIGhlaWdodD0iNTUwIiBmaWxsPSJub25lIj48ZyBjbGlwLXBhdGg9InVybCgjYSkiIG9wYWNpdHk9Ii40Ij48bWFzayBpZD0iYyIgd2lkdGg9Ijg5MyIgaGVpZ2h0PSI5NDIiIHg9IjEyOSIgeT0iLTIyOSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTphbHBoYSI+PHBhdGggZmlsbD0idXJsKCNiKSIgZD0iTTEyOS0yMjloODkzdjk0MkgxMjl6Ii8+PC9tYXNrPjxnIHN0cm9rZT0iI0I2NzRGRiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBzdHJva2Utd2lkdGg9IjYzLjc1IiBtYXNrPSJ1cmwoI2MpIj48Y2lyY2xlIGN4PSI1MzUuMjUiIGN5PSIzMDYuMjUiIHI9IjM2Mi45MTQiIG9wYWNpdHk9Ii4yIiB0cmFuc2Zvcm09InJvdGF0ZSgxMTguNDczIDUzNS4yNSAzMDYuMjUpIi8+PHBhdGggZD0iTTIwMy4zOTkgMTU5LjM0MmEzNjIuOTE1IDM2Mi45MTUgMCAwIDEgNjM4LjM2NS00Ny40MDMgMzYyLjkxNSAzNjIuOTE1IDAgMCAxIDUyLjA1MiAyNTAuMzE4IiBvcGFjaXR5PSIuMiIvPjxwYXRoIGQ9Ik0yNTMuNDg5IDc3LjUyQTM2Mi45MTUgMzYyLjkxNSAwIDAgMSA3NjMuOTggMjQuNDkiIG9wYWNpdHk9Ii4yIi8+PC9nPjwvZz48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImIiIHgxPSIxMjkiIHgyPSIzMjIiIHkxPSIyNDIiIHkyPSIzMjYuNSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIHN0b3AtY29sb3I9IiNEOUQ5RDkiLz48c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiM3MzczNzMiIHN0b3Atb3BhY2l0eT0iMCIvPjwvbGluZWFyR3JhZGllbnQ+PGNsaXBQYXRoIGlkPSJhIj48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMC0yMjloNTQ0djc3OUgweiIvPjwvY2xpcFBhdGg+PC9kZWZzPjwvc3ZnPg=="
            />
          </div>
        </Container>
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 60 }}
        dashedBorder
        examName="SAT"
        navigationItems={props.navigationItems}
        navigationStyle={{ background: 'transparent' }}
        notice={props.notice}
      >
        <div style={{ zIndex: 1 }}>
          <HeroSection
            header="Beat the SAT in Just 20 Minutes a Day"
            interactivePrompt={{
              answerChoices: ['52', '59', '60', '61'],
              prompt: 'A shipment of watermelons weighs 899 pounds. If each watermelon weighs at least 15 pounds, what is the greatest number of watermelons that could be in the shipment?',
              subject: 'Math'
            }}
            startingOffer={props.startingOffer}
            subHeader={
              <>
                Improve your digital SAT score by at least 100 points with realistic practice questions, an
                {' '}<UnderlinedText color="#B674FF">AI-powered tutor</UnderlinedText>
                , engaging video lessons, and top-rated mobile apps—at
                {' '}<UnderlinedText color="#B674FF">third of the price</UnderlinedText>
                {' '}of other "premium" options.
              </>
            }
          />
        </div>

        <SocialProofSection
          quotes={[
            {
              content: <>"Magoosh gave me challenging practice problems as well as a <mark style={{ background: '#FCF0BF' }}>new set of practice problems</mark> compared to other studying websites. It also had <mark style={{ background: '#FCF0BF' }}>good explanations</mark>, which were both videos and written."</>,
              scores: [{ name: 'SAT Score', score: '1520' }],
              studentName: 'Nishita B.'
            },
            {
              content: <>"Magoosh provided me with <mark style={{ background: '#FCF0BF' }}>practice tests</mark> that simulate the actual exam.  It also <mark style={{ background: '#FCF0BF' }}>predicted my score range</mark> and suggested areas I should focus on."</>,
              scores: [{ name: 'SAT Score', score: '1460' }],
              studentName: 'Ekene I.'
            },
            {
              content: <>"Magoosh offered <mark style={{ background: '#FCF0BF' }}>great lessons</mark> which went in-depth on each type of question. They would <mark style={{ background: '#FCF0BF' }}>respond very quickly</mark> via email. It is definitely a program worth investing in if you would like to see <mark style={{ background: '#FCF0BF' }}>improvement.</mark>"</>,
              scores: [{ name: 'SAT Score', score: '1460' }],
              studentName: 'Ashita G.'
            },
            {
              content: <>"Magoosh helped my son to <mark style={{ background: '#FCF0BF' }}>identify where he needed the most practice</mark>. The videos were short and easy to understand. The ability to engage in <mark style={{ background: '#FCF0BF' }}>short practice sessions whenever he had time</mark> was very helpful."</>,
              scores: [{ name: 'SAT Score', score: '1410' }],
              studentName: 'Maeghan M.'
            }
          ]}
          tagline="Purchased by 100,000+ high school students"
        />

        <div className="d-flex flex-column gap-m">
          <CompetitorComparisonSection
            competitorShortfalls={[
              'No AI support to guide your studying',
              'Text-based explanations only',
              'Not optimized for mobile',
              <>Costs upwards of <strong>$300</strong></>
            ]}
            magooshFeatures={[
              'Get a Magoosh-trained AI tutor that works with you until you get it',
              'Video and text-based lessons to support multiple learning styles',
              'Download our mobile apps to practice on the go',
              <>Starts at an affordable <strong>{formatOfferPrice(props.startingOffer)}</strong></>
            ]}
            pointIncrease={100}
           />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <ValuePropsSection />

        <AISection />

        <AppsSection />

        <div className="u-margin-V-l">
          <InstructorsSection />
        </div>

        <VideoBasedLearningSection />

        <WallOfLoveSection />

        <div className={marketingStyles.fullContainerWidth} style={{ marginBottom: 20 }}>
          <div
            className={cx(
              marketingStyles.dashedBorderHorizontal,
              'align-items-center d-flex gap-s justify-content-center u-padding-V-l'
            )}
          >
            <Container>
              <EducatorsSection />
            </Container>
          </div>
        </div>

        <FeaturesSection />

        <div className="d-flex flex-column gap-m">
          <GuaranteeSection pointIncrease={100} />
          <div className={cx(marketingStyles.dashedBorderBottom, marketingStyles.fullContainerWidth)} style={{ height: 1 }} />
        </div>

        <FAQSection
          entries={[
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: "We compare your performance in the program with data from Magoosh students' actual scores and performance in our program and then provide you with a predicted range for the actual test.",
              question: 'How does your score predictor work?'
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Yes! Check out our <a href="https://act.magoosh.com">ACT website</a> to learn more.
                </>
              ),
              question: 'Does Magoosh also offer ACT prep?'
            }
          ]}
          footerNote={<>A full list of frequently asked questions and their answers can be found on the <a className="text-body" href="/plans#faq">plans page</a>.</>}
        />
      </PageLayout>
    </div>
  );
};
